import {
  useEffect,
  useEffectEvent,
  useRef,
  useState,
  type FormEvent,
} from "react";

import { plainName, spokenName } from "../lib/program-names";
import type { CogentLive } from "./chat/chat-view";
import type { CogentWidgetProps, Status } from "./widget-types";
import { doingOf, statusLineOf, activityOf } from "./widget-activity";

export function useWidgetActions({
  props,
  live,
}: {
  props: CogentWidgetProps;
  live: CogentLive | null;
}) {
  const name = props.cogent?.name ?? "your cogent";
  const [question, setQuestion] = useState("");
  const [releasing, setReleasing] = useState(false);
  const [selectedVersion, setVersionId] = useState("");
  const versionId = props.releasable.some(
    (version) => version.id === selectedVersion,
  )
    ? selectedVersion
    : (props.releasable[0]?.id ?? "");
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const busy = status.kind === "busy";
  const doing = doingOf({
    live,
    cogent: props.cogent,
  });
  const working = doing !== null;
  const releaseAsked = useRef(false);
  const wasWorking = useRef(false);
  const releaseSettled = useEffectEvent(() => props.onReleaseSettled?.());
  useEffect(() => {
    if (wasWorking.current && !working && releaseAsked.current)
      releaseSettled();

    wasWorking.current = working;
  }, [working]);
  const run = async ({
    busyText,
    work,
  }: {
    busyText: string;
    work: () => Promise<string>;
  }) => {
    setStatus({ kind: "busy", text: busyText });
    await work().then(
      (text) => setStatus({ kind: "done", text }),
      (error: unknown) =>
        setStatus({
          kind: "error",
          text: error instanceof Error ? error.message : String(error),
        }),
    );
  };
  const release = () =>
    run({
      busyText: "Releasing…",
      work: async () => {
        const version = props.releasable.find(
          (candidate) => candidate.id === versionId,
        );

        if (!version) throw new Error("Pick a version to release.");

        if (!live) throw new Error(`${name} isn't reachable yet.`);

        const sent = await live.send({
          text: `Please release ${spokenName(version.label)} in ${props.coworld.name}.`,
        });

        if (!sent) throw new Error(`Couldn't tell ${name}. Try again.`);

        releaseAsked.current = true;
        setReleasing(false);
        return `Asked ${name} to release ${plainName(version.label)}. It will ask you here before it does.`;
      },
    });
  const ask = (event: FormEvent) => {
    event.preventDefault();
    const text = question.trim();

    if (!text || !live) return;

    setQuestion("");
    void live.send({ text }).then((sent) => {
      if (!sent) setQuestion(text);
    });
  };
  const start = live?.start;
  const wake = start
    ? () =>
        void run({
          busyText: "Starting…",
          work: async () => {
            await start();
            return `${name} connected.`;
          },
        })
    : null;
  return {
    question,
    setQuestion,
    status,
    busy,
    working,
    statusLine: statusLineOf({
      live,
      doing,
    }),
    offline: live?.status.kind === "offline" && live.status.canStart,
    activity: activityOf({
      live,
      doing,
      busy,
    }),
    releasing,
    setReleasing,
    versionId,
    setVersionId,
    run,
    release,
    ask,
    wake,
  };
}

export type WidgetActions = ReturnType<typeof useWidgetActions>;
